import { Group, Modal, Stack } from '@mantine/core';
import { newCOLORS } from '../../../../styles/colors';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';

type SaveTemplateConfirmModalProps = {
  opened: boolean;
  isPending: boolean;
  childPageCount: number;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Confirmation modal shown before persisting any template-config edit. Warns
 * the user that saving cascades to every dashboard created from this
 * template; phrasing adapts to `childPageCount` (none / one / many).
 *
 * @param {SaveTemplateConfirmModalProps} props - Open state, pending flag, child count, and the cancel/confirm callbacks
 * @returns {JSX.Element} The confirmation Modal
 */
export const SaveTemplateConfirmModal = ({
  opened,
  isPending,
  childPageCount,
  onCancel,
  onConfirm,
}: SaveTemplateConfirmModalProps) => {
  const cascadeMessage =
    childPageCount === 0
      ? 'No dashboards will be affected by this change.'
      : `This will update ${childPageCount} ${childPageCount === 1 ? 'dashboard' : 'dashboards'} created from this template. Existing customizations will be overwritten.`;

  return (
    <Modal opened={opened} onClose={onCancel} title="Save template changes?" centered>
      <Stack>
        <Text>{cascadeMessage}</Text>
        <Group justify="flex-end" gap="sm">
          <Button
            variant="outline"
            size="sm"
            radius="xl"
            color={newCOLORS.darkGray}
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            radius="xl"
            onClick={onConfirm}
            disabled={isPending}
            leftSection={<Icon name="check_circle" size={16} color="white" />}
          >
            {isPending ? 'Saving...' : 'Save & Update'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
